import React from 'react';
import { Check, Radio } from '../FormControls';
import Field from '../Field';
import {
  SETTINGS_WORDING as W, holdDaysDraft, policyNumberDraft, policyOptions, policyWord as p,
} from '../../domain/studioSettings';
import { useCommittedText, useCommittedValue, blurOnEnter } from './useCommitted';
import s from './settings.module.css';

// Business Settings → Policies (#766), for the Family Portal of #543. Drawn
// only while the portal is open (#769, SettingsPage.jsx), so it carries no
// "not open yet" line.
//
// What MMS's tab holds, its defaults, and where we knowingly differ — no
// Booking Form, so "Allow event booking from" is one checkbox; no in-app
// notification ticks; our own bounds on the three number boxes — are written
// once, beside the rules, at `POLICIES_GROUP` in core/src/settings.rs. The
// words and the radios' options are the core's too (`policyWord`,
// `policyOptions`), so the phones say the same.
//
// The layout is MMS's: Booking Policy, then Cancellation Policy with its
// Preferences, Policy Text, the before-deadline block, the Deadline, the
// after-deadline block and Cancelled Event Category. MMS's third section,
// Make-Up Credit Policy, stays on Email & SMS beside the rest of the `makeup`
// group.
//
// Weekly time slot hold duration is here, where MMS lists it, under Booking
// Policy. It is `scheduling.weeklyHoldDays`, and while the portal is closed and
// this tab is not drawn it is on General → Event Scheduling
// (EventSchedulingPanel.jsx).
//
// The teacher's two portal email notices sit at the foot of this tab under
// MMS's heading. MMS keeps them on each teacher's My Profile; there is no My
// Profile here, and a studio is one teacher's (`TEACHER_NOTICES_GROUP`).
//
// Saved on change, a typed box on blur, like every Business Settings panel
// (SettingsPage.jsx), each `onChange` naming only the leaf that changed.

function NumberBox({ id, value, unit, min, max, onCommit, parse }) {
  const box = useCommittedValue(value, parse, onCommit);
  return (
    <div className={s.numberRow}>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        className={s.numberInput}
        {...box.field}
        onKeyDown={blurOnEnter}
      />
      <span className={s.unit}>{unit}</span>
    </div>
  );
}

function RadioList({ name, kind, value, onChange }) {
  return policyOptions(kind).map((o) => (
    <Radio
      key={o.id}
      name={name}
      label={o.label}
      checked={value === o.id}
      onChange={() => onChange(o.id)}
    />
  ));
}

// One side of the deadline: what happens to the attendance, and to the slot.
function DeadlineSide({ side, policies, onChange }) {
  const attendance = `${side}DeadlineAttendance`;
  const slot = `${side}DeadlineSlot`;
  return (
    <div className={s.group}>
      <div className={s.groupLabel}>{p(`${side}DeadlineTitle`)}</div>
      <div className={s.groupHelp}>{p(`${side}DeadlineHelp`)}</div>
      <div className={s.groupLabel}>{p(`${side}AttendanceQuestion`)}</div>
      <RadioList
        name={`policy-${side}-attendance`}
        kind={attendance}
        value={policies[attendance]}
        onChange={(v) => onChange({ [attendance]: v })}
      />
      <div className={s.groupLabel}>{p(`${side}SlotQuestion`)}</div>
      <RadioList
        name={`policy-${side}-slot`}
        kind="cancelSlot"
        value={policies[slot]}
        onChange={(v) => onChange({ [slot]: v })}
      />
    </div>
  );
}

export default function PoliciesPanel({
  policies, scheduling, notices, categories = [], onChange, onChangeScheduling, onChangeNotices,
}) {
  const policyText = useCommittedText(policies.policyText, (v) => onChange({ policyText: v || null }));
  const number = (leaf) => ({
    value: policies[leaf],
    parse: (draft) => policyNumberDraft(leaf, draft),
    onCommit: (v) => onChange({ [leaf]: v }),
  });
  // A category since deleted still reads as chosen, by its id, rather than
  // the select quietly showing "No Change" over a stored id.
  const chosen = policies.cancelledCategoryId;
  const missing = chosen && !categories.some((c) => c.id === chosen);

  return (
    <>
      <div className={s.panel}>
        <div className={s.panelTitle}>{p('bookingPolicy')}</div>

        <div className={s.group}>
          <label className={s.groupLabel} htmlFor="policy-min-advance">{p('minAdvanceLabel')}</label>
          <NumberBox id="policy-min-advance" unit={p('hoursUnit')} min="0" max="720" {...number('minAdvanceHours')} />
          <div className={s.groupHelp}>{p('minAdvanceHelp')}</div>
        </div>

        <div className={s.group}>
          <label className={s.groupLabel} htmlFor="policy-max-advance">{p('maxAdvanceLabel')}</label>
          <NumberBox id="policy-max-advance" unit={p('daysUnit')} min="1" max="365" {...number('maxAdvanceDays')} />
          <div className={s.groupHelp}>{p('maxAdvanceHelp')}</div>
        </div>

        <div className={s.group}>
          <label className={s.groupLabel} htmlFor="weekly-hold-days">{W.holdLabel}</label>
          <NumberBox
            id="weekly-hold-days"
            unit={W.holdUnit}
            min="1"
            max="60"
            value={scheduling.weeklyHoldDays}
            parse={(draft) => holdDaysDraft(draft)}
            onCommit={(weeklyHoldDays) => onChangeScheduling({ weeklyHoldDays })}
          />
          <div className={s.groupHelp}>{W.holdHelp}</div>
        </div>

        <div className={s.group}>
          <Check
            checked={policies.portalBooking}
            onChange={(e) => onChange({ portalBooking: e.target.checked })}
            label={p('portalBookingCheck')}
          />
          <Check
            checked={policies.makeupCreditsOnly}
            onChange={(e) => onChange({ makeupCreditsOnly: e.target.checked })}
            label={p('makeupOnlyCheck')}
          />
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>{p('cancellationPolicy')}</div>
        <div className={s.groupHelp}>{p('cancellationPolicyBlurb')}</div>

        <div className={s.group}>
          <div className={s.groupLabel}>{p('preferences')}</div>
          <Check
            checked={policies.portalCancellation}
            onChange={(e) => onChange({ portalCancellation: e.target.checked })}
            label={p('portalCancellationCheck')}
          />
        </div>

        <div className={s.group}>
          <Field label={p('policyText')} htmlFor="policy-text">
            {/* No blurOnEnter: a policy is paragraphs. */}
            <textarea
              id="policy-text"
              className={s.textarea}
              rows={4}
              maxLength={4999}
              placeholder={p('policyTextPlaceholder')}
              {...policyText.field}
            />
          </Field>
        </div>

        <DeadlineSide side="before" policies={policies} onChange={onChange} />

        <div className={s.group}>
          <label className={s.groupLabel} htmlFor="policy-deadline">{p('deadlineLabel')}</label>
          <NumberBox id="policy-deadline" unit={p('deadlineUnit')} min="0" max="720" {...number('deadlineHours')} />
        </div>

        <DeadlineSide side="after" policies={policies} onChange={onChange} />

        <div className={s.group}>
          <Field label={p('cancelledCategoryLabel')} htmlFor="policy-cancelled-category">
            <select
              id="policy-cancelled-category"
              className={s.windowSelect}
              value={chosen || ''}
              onChange={(e) => onChange({ cancelledCategoryId: e.target.value || null })}
            >
              <option value="">{p('cancelledCategoryNone')}</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              {missing ? <option value={chosen}>{chosen}</option> : null}
            </select>
          </Field>
          <div className={s.groupHelp}>{p('cancelledCategoryHelp')}</div>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>{p('teacherNotices')}</div>
        <div className={s.group}>
          <Check
            checked={notices.portalBooking}
            onChange={(e) => onChangeNotices({ portalBooking: e.target.checked })}
            label={p('noticeBookingCheck')}
          />
          <Check
            checked={notices.portalCancellation}
            onChange={(e) => onChangeNotices({ portalCancellation: e.target.checked })}
            label={p('noticeCancellationCheck')}
          />
        </div>
      </div>
    </>
  );
}
